import { useMemo } from 'react'
import { ArrowRightIcon, cn, Label, Paragraph } from '@quickmd-llc/design-system'
import type { MedicationStaffApiOutContract } from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'
import { useGetAdminsFulfillmentpartners } from '@/api/generated/fulfillment-partner/fulfillment-partner'
import { SearchComponent } from '@/components/SearchComponent/SearchComponent'
import { MedicationCard } from './MedicationCard'

interface MedicationListPanelProps {
  medications: MedicationStaffApiOutContract[]
  selectedId: string | null
  search: string
  onSearchChange: (value: string) => void
  onSelect: (medication: MedicationStaffApiOutContract) => void
  onManagePartners: () => void
}

export function MedicationListPanel({
  medications,
  selectedId,
  search,
  onSearchChange,
  onSelect,
  onManagePartners,
}: MedicationListPanelProps) {
  const { data: partners } = useGetAdminsFulfillmentpartners()

  const filtered = useMemo(() => {
    if (!search) return medications
    const term = search.toLowerCase()
    return medications.filter(
      (m) =>
        (m.Name ?? '').toLowerCase().includes(term) ||
        (m.NationalDrugCode ?? '').toLowerCase().includes(term) ||
        (m.GenericCodeNumber ?? '').toLowerCase().includes(term),
    )
  }, [medications, search])

  const partnerCount = partners?.length ?? 0

  return (
    <div className='flex min-h-0 flex-col rounded-large border border-border-medium bg-white'>
      {/* Search */}
      <div className='p-sp-2'>
        <SearchComponent
          placeholder='Search by name or drug code'
          searchText={search}
          onChangeSearchText={(e) => onSearchChange(e.target.value)}
          additionalClassName={cn('w-full')}
        />
      </div>

      {/* Count */}
      <div className='border-b border-border-medium px-sp-2 py-sp-1'>
        <Label size='lg'>
          {filtered.length} {filtered.length === 1 ? 'medication' : 'medications'}
        </Label>
      </div>

      {/* List */}
      <div className='flex-1 overflow-y-auto'>
        {filtered.length === 0 ?
          <div className='flex h-full items-center justify-center p-sp-2'>
            <Paragraph size='sm' className='text-center text-text-light'>
              {search ? 'No medications match your search.' : 'No medications yet.'}
            </Paragraph>
          </div>
        : <div className='flex flex-col gap-sp-1 p-sp-1'>
            {filtered.map((med) => (
              <MedicationCard key={med.Id} medication={med} isSelected={med.Id === selectedId} onClick={onSelect} />
            ))}
          </div>
        }
      </div>

      {/* Manage fulfillment partners */}
      <button
        type='button'
        onClick={onManagePartners}
        className='flex items-center justify-between rounded-b-large border-t border-border-dark px-sp-2 py-sp-1.5 text-left hover:bg-coastal-blue-10'>
        <Label size='sm'>
          {partnerCount === 0 ? 'Add fulfillment partners' : `Fulfillment partners (${partnerCount})`}
        </Label>
        <ArrowRightIcon iconSize='s' className='text-text-light' />
      </button>
    </div>
  )
}
